import { Assets, Container, Graphics, Sprite } from "pixi.js";
import type { Texture } from "pixi.js";
import type {
  ProjectileSnapshot,
  ShipSnapshot,
  WorldSnapshot,
} from "@frontier/protocol/contracts";
import type { Point } from "./geometry";
import { interpolateAngle } from "./geometry";
import { projectileAppearance } from "./projectile-appearance";
import { AreaEffects } from "./area-effects";
import { LaserBeams } from "./laser-beams";
import type { RenderedCombatShips } from "./combat-ship-pose";
import { ShieldBubbles } from "./shield-bubbles";
import { CombatVfx, combatPointVisible } from "./combat-vfx";
import type { CombatViewport } from "./combat-vfx";
import {
  COMBAT_VFX_LIMITS,
  confirmedShipImpacts,
} from "./combat-vfx-presentation";

export interface CombatTextures {
  pulse: Texture;
  laser: Texture;
  missile: Texture;
  mine: Texture;
  plasma: Texture;
  explosion: readonly Texture[];
}
export async function loadCombatTextures(): Promise<CombatTextures> {
  const [pulse, laser, missile, mine, plasma, explosion] = await Promise.all([
    Assets.load<Texture>("/assets/combat/pulse.png"),
    Assets.load<Texture>("/assets/combat/laser.png"),
    Assets.load<Texture>("/assets/combat/missile.png"),
    Assets.load<Texture>("/assets/combat/mine.png"),
    Assets.load<Texture>("/assets/combat/plasma.png"),
    Promise.all(
      [0, 1, 2, 3, 4].map((frame) =>
        Assets.load<Texture>(`/assets/combat/explosion-${frame}.png`),
      ),
    ),
  ]);
  return { pulse, laser, missile, mine, plasma, explosion };
}

export class CombatEffects {
  readonly container = new Container();
  private readonly warning = new Graphics();
  private readonly areas = new AreaEffects();
  private readonly lasers = new LaserBeams();
  private readonly shields = new ShieldBubbles();
  private readonly shots = new Map<string, Sprite>();
  private readonly spareShots: Sprite[] = [];
  private readonly vfx: CombatVfx;
  private previousShips = new Map<string, ShipSnapshot>();
  private current: readonly ProjectileSnapshot[] = [];
  private previous = new Map<string, ProjectileSnapshot>();
  private receivedAt = 0;

  constructor(private readonly textures: CombatTextures) {
    this.vfx = new CombatVfx(textures.explosion);
    this.container.addChild(
      this.areas.container,
      this.warning,
      this.vfx.trails,
      this.lasers.container,
      this.vfx.heads,
      this.shields.container,
      this.vfx.container,
    );
  }

  receive(snapshot: WorldSnapshot, now: number): void {
    this.previous = new Map(this.current.map((shot) => [shot.id, shot]));
    this.current = snapshot.projectiles;
    this.areas.receive(snapshot.combatEffects, now);
    const ships = [...snapshot.players, ...snapshot.npcs];
    const impacts = confirmedShipImpacts(this.previousShips, ships);
    for (const impact of impacts)
      if (impact.kind === "hull") this.vfx.impact(impact, now);
    this.previousShips = new Map(ships.map((ship) => [ship.id, ship]));
    this.shields.receive(
      impacts,
      this.previousShips,
      [...this.previous.values(), ...this.current],
      now,
    );
    this.lasers.receive(this.current, this.previousShips, now);
    this.receivedAt = now;
    const ids = new Set(
      this.current
        .filter((shot) => shot.kind !== "laser")
        .map((shot) => shot.id),
    );
    for (const [id, sprite] of this.shots) {
      if (ids.has(id)) continue;
      sprite.visible = false;
      if (this.spareShots.length < COMBAT_VFX_LIMITS.spareProjectiles)
        this.spareShots.push(sprite);
      else sprite.destroy();
      this.shots.delete(id);
    }
    for (const shot of this.current) {
      if (shot.kind === "laser") continue;
      if (this.shots.has(shot.id)) continue;
      const appearance = projectileAppearance(shot.kind);
      const sprite = this.spareShots.pop() ?? new Sprite({ anchor: 0.5 });
      sprite.texture = this.textures[appearance.texture];
      sprite.tint = appearance.tint;
      sprite.label = `projectile:${shot.id}`;
      sprite.visible = false;
      sprite.height = appearance.length;
      sprite.scale.x = sprite.scale.y;
      this.shots.set(shot.id, sprite);
      if (!sprite.parent)
        this.container.addChildAt(
          sprite,
          this.container.getChildIndex(this.vfx.heads),
        );
    }
  }

  explode(point: Point, size: number, now: number): void {
    this.vfx.explode(point, size, now);
  }

  render(
    now: number,
    viewport?: CombatViewport,
    reducedMotion = false,
    renderedShips?: RenderedCombatShips,
  ): void {
    this.vfx.beginFrame(viewport);
    this.areas.render(now);
    this.lasers.render(now, viewport, renderedShips);
    this.shields.render(now, viewport, reducedMotion, renderedShips);
    this.warning.clear();
    const fraction = Math.max(0, Math.min(1, (now - this.receivedAt) / 100));
    for (const shot of this.current) {
      if (shot.kind === "laser") continue;
      const sprite = this.shots.get(shot.id);
      if (!sprite) continue;
      const old = this.previous.get(shot.id) ?? shot;
      sprite.position.set(
        old.x + (shot.x - old.x) * fraction,
        old.y + (shot.y - old.y) * fraction,
      );
      sprite.rotation =
        interpolateAngle(old.angle, shot.angle, fraction) + Math.PI / 2;
      const appearance = projectileAppearance(shot.kind);
      sprite.visible = combatPointVisible(sprite, appearance.length, viewport);
      if (!sprite.visible) continue;
      if (shot.kind === "rail" || shot.kind === "ion")
        this.vfx.projectileHead(
          sprite,
          appearance.tint,
          shot.kind === "rail" ? 6 : 8,
        );
      if (shot.kind === "mine")
        this.warning
          .circle(
            sprite.x,
            sprite.y,
            22 + (reducedMotion ? 0 : Math.sin(now / 130) * 2),
          )
          .stroke({ color: 0xffac69, width: 1.5, alpha: 0.65 });
      else if (!reducedMotion)
        this.vfx.trail(
          sprite,
          sprite.rotation - Math.PI / 2,
          Math.min(110, appearance.length * 1.6),
          appearance.tint,
        );
    }
    this.vfx.render(now, reducedMotion);
  }

  destroy(): void {
    this.lasers.destroy();
    this.shields.destroy();
    this.vfx.destroy();
    this.container.destroy({ children: true });
  }
}
